import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Features } from '@app/core/user-permissions/user-permissions.model';
import { UserPermissionsService } from '@app/core/user-permissions/user-permissions.service';
import { DirectPlan } from '@app/modules/direct/models/direct-subscription.interface';
import { DirectSubscriptionService } from '@app/modules/direct/services/direct-subscription.service';
import { DeviceDetectionService } from '@app/shared/services/device-detection/device-detection.service';
import { PosthogService } from '@app/shared/services/posthog/posthog.service';
import { filter, take } from 'rxjs/operators';

interface NavItem {
  id: string;
  icon: string;
  useIconComponent?: boolean;
  heading: string;
  altHeading?: string;
  permission?: Features;
  fragmentBased?: boolean;
  children?: {
    id: string;
    heading: string;
    altHeading?: string;
    allowedWhenMuted?: boolean;
    hidden?: boolean;
    permission?: Features;
  }[];
  allowedWhenMuted?: boolean; // Can we use this link when the property is muted
  new?: boolean;
  badge?: string;
  hidden?: boolean;
}

@Component({
  standalone: false,
  selector: 'sbnb-property-detail-subnav',
  templateUrl: './property-detail-subnav.component.html',
  styleUrls: ['./property-detail-subnav.component.scss'],
})
export class PropertyDetailSubnavComponent implements OnInit, OnChanges {
  @Input() activeComponent = 'details';
  @Input() onboardingStatus: boolean;
  @Input() propertyMuted: boolean; // Disabled as the property is muted
  @Input() bookingListing: boolean; // Property has at least 1 booking.com listing
  @Input() canManageAvailability: boolean;

  activeParent: string;
  perms$ = this.userPermissionsService;
  Features = Features;

  navigation: NavItem[];
  onboardingNavigation: NavItem[];

  @Output() navChanged: EventEmitter<{ path: string; heading: string }> = new EventEmitter();
  @Output() onboardingChanged: EventEmitter<boolean> = new EventEmitter();
  enableTaxes = false;

  /* remove when mobile nav tabs are stable */
  public featureFlagMobileNavTabsEnabled = false;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private userPermissionsService: UserPermissionsService,
    private directSubscription: DirectSubscriptionService,
    private posthog: PosthogService,
    public deviceDetectionService: DeviceDetectionService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activeComponent']) {
      this.setParentItem(changes['activeComponent'].currentValue);
    }

    if (changes['propertyMuted'] || changes['bookingListing'] || changes['canManageAvailability']) {
      this.buildNavigation();
      this.checkEnableTaxes();
    }
  }

  checkEnableTaxes() {
    this.directSubscription.subscriptionStatus$
      .pipe(
        filter((sub) => sub !== null),
        take(1)
      )
      .subscribe((sub) => {
        if (sub.plan === DirectPlan.Basic) {
          this.enableTaxes = true;
          this.buildNavigation();
        }
      });
  }

  setParentItem(activeComponent: string) {
    if (!this.navigation || this.navigation.length === 0) {
      return;
    }

    this.navigation.forEach((parent) => {
      if (!parent.children || parent.children.length === 0) {
        return;
      }

      if (parent.fragmentBased) {
        this.activeParent = this.activeComponent;
      }

      parent.children.forEach((child) => {
        if (child.id === activeComponent) {
          this.activeParent = parent.id;
        }
      });
    });
  }

  buildNavigation() {
    this.navigation = [
      {
        id: 'merge-match',
        icon: 'merge',
        heading: 'Merge & match',
        allowedWhenMuted: true,
        children: [],
      },
      {
        id: 'details',
        icon: 'properties',
        useIconComponent: true,
        heading: 'Details',
        altHeading: 'Property details',
        allowedWhenMuted: false,
        fragmentBased: true,
        children: [
          {
            id: 'tags',
            heading: 'Tags',
          },
          {
            id: 'photos',
            heading: 'Photos',
          },
          {
            id: 'basics',
            heading: 'Basic info',
          },
          {
            id: 'location',
            heading: 'Location',
          },
          {
            id: 'features-rooms',
            heading: 'Features & rooms',
          },
        ],
      },
      {
        id: 'knowledge',
        icon: 'nav-knowledge',
        heading: 'Knowledge Hub',
        allowedWhenMuted: false,
      },
      {
        id: 'availability-rules',
        heading: 'Availability',
        icon: 'calendar-availability',
        useIconComponent: true,
        children: [],
        allowedWhenMuted: false,
        permission: Features.PERM_CALENDAR_UPDATE_AVAILABILITY,
        hidden: !this.canManageAvailability,
      },
      {
        id: 'calendar',
        icon: 'calendar',
        useIconComponent: true,
        heading: 'Calendar',
        permission: Features.PERM_CALENDAR,
        children: [],
        allowedWhenMuted: true,
      },
      {
        id: 'pricing',
        icon: 'subtotal',
        heading: 'Pricing',
        allowedWhenMuted: false,
        fragmentBased: true,
        children: [
          {
            id: 'listing-markups',
            heading: 'Markups',
            allowedWhenMuted: false,
            permission: Features.PERM_CALENDAR_UPDATE_PRICING_AND_AVAIL,
          },
          {
            id: 'bcom-rate-plans',
            heading: 'Rate plans',
            allowedWhenMuted: false,
            hidden: !this.bookingListing,
            permission: Features.PERM_CALENDAR_UPDATE_PRICING_AND_AVAIL,
          },
          {
            id: 'security-deposit',
            heading: 'Security deposit',
            allowedWhenMuted: false,
          },
          {
            id: 'fees',
            heading: 'Fees',
            allowedWhenMuted: false,
          },
          {
            id: 'discounts',
            heading: 'Discounts',
            allowedWhenMuted: false,
          },
          {
            id: 'taxes',
            heading: 'Taxes',
            allowedWhenMuted: false,
            hidden: !this.enableTaxes,
          },
        ],
      },
      {
        id: 'messaging-rules',
        heading: 'Guest Experience',
        icon: 'heart',
        useIconComponent: true,
        permission: Features.PERM_GUEST_EXPERIENCE,
        allowedWhenMuted: false,
        children: [
          {
            id: 'messaging-rules',
            heading: 'Messaging rules',
            allowedWhenMuted: false,
          },
          {
            id: 'review-rules',
            heading: 'Review rules',
            allowedWhenMuted: false,
          },
          {
            id: 'canned-responses',
            heading: 'Canned responses',
            allowedWhenMuted: false,
          },
        ],
      },
      {
        id: 'task-rules',
        heading: 'Operations',
        permission: Features.PERM_OPERATIONS,
        icon: 'ops',
        useIconComponent: true,
        allowedWhenMuted: false,
        children: [
          {
            id: 'task-rules',
            heading: 'Task rules',
            allowedWhenMuted: false,
          },
          {
            id: 'team',
            heading: 'Team',
            allowedWhenMuted: false,
          },
          {
            id: 'notification-rules',
            heading: 'Notification rules',
            altHeading: 'Team notification rules',
            allowedWhenMuted: false,
          },
        ],
      },
      {
        id: 'custom-codes',
        heading: 'Custom Codes',
        permission: Features.PERM_GUEST_EXPERIENCE,
        icon: 'custom-code',
        children: [],
        allowedWhenMuted: false,
      },
      {
        id: 'direct',
        icon: 'direct-simple',
        useIconComponent: true,
        heading: 'Direct',
        children: [],
        allowedWhenMuted: false,
      },
    ];
  }

  ngOnInit() {
    this.onboardingNavigation = [
      {
        id: 'messaging-rules',
        heading: 'Messaging rules',
        permission: Features.PERM_GUEST_EXPERIENCE,
        icon: 'message',
      },
      {
        id: 'review-rules',
        heading: 'Review rules',
        permission: Features.PERM_GUEST_EXPERIENCE,
        icon: 'review',
      },
      {
        id: 'canned-responses',
        heading: 'Canned responses',
        permission: Features.PERM_GUEST_EXPERIENCE,
        icon: 'canned-response',
      },
      {
        id: 'task-rules',
        heading: 'Task rules',
        permission: Features.PERM_OPERATIONS,
        icon: 'task-rules',
      },
      {
        id: 'team',
        heading: 'Team',
        permission: Features.PERM_OPERATIONS,
        icon: 'team',
      },
      {
        id: 'notification-rules',
        heading: 'Notification rules',
        permission: Features.PERM_OPERATIONS,
        icon: 'bell',
      },
      {
        id: 'custom-codes',
        heading: 'Custom codes',
        permission: Features.PERM_GUEST_EXPERIENCE,
        icon: 'custom-code',
      },
    ];

    this.setParentItem(this.activeComponent);

    this.navChanged.emit({
      path: this.activeComponent,
      heading: this.findNavItemHeading(this.activeComponent),
    });

    /* remove when mobile nav tabs are stable */
    this.posthog.mobileNavTabsEnabled$.pipe(take(1)).subscribe((enabled) => {
      this.featureFlagMobileNavTabsEnabled = !!enabled;
    });
  }

  changeActiveComponentFromChild(newId: string, newParent: NavItem) {
    // if child of a fragmentBased navigation item, scroll to it.
    // Note: fragmentBased children are visible when the parent is the current item
    if (newParent.fragmentBased) {
      document.getElementById(newId)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      this.changeActiveComponent(newId, newParent);
    }
  }

  changeActiveComponent(newId: string, newParent: NavItem) {
    // if root level fragmentBased item clicked & is currenty active, scroll to page id (top)
    if (newParent.fragmentBased && newParent.id === newId && this.activeComponent === newId) {
      document.getElementById('property-details')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    this.activeParent = newParent.id;

    this.activeComponent = newId;

    this.navChanged.emit({
      path: this.activeComponent,
      heading: this.findNavItemHeading(newId),
    });
  }

  private findNavItemHeading(path: string) {
    for (const nav of this.navigation) {
      if (nav.id === this.activeParent && nav.children?.length && !nav.fragmentBased) {
        const child = nav.children.find((child) => child.id === path);
        return child?.altHeading ?? child?.heading;
      }
      if (nav.id === path) {
        return nav.altHeading ?? nav.heading;
      }
    }
  }

  leaveOnboarding() {
    this.onboardingStatus = false;
    this.onboardingChanged.emit(this.onboardingStatus);
  }

  goBack() {
    this.router.navigate(['/properties/segments/default']);
  }
}
